import { Extension, InputRule } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { isConditionBracket, parseConditionString } from '../utils/templateParser';

export const SyntaxAutoSync = Extension.create({
  name: 'syntaxAutoSync',

  addInputRules() {
    const rules = [];

    // Signature directive auto sync:
    // 1. Conditional signature: { => ! 1.0 1.0 signature.png [<<Arbitrator name>> == name ] }
    // 2. Aligned signature: => ! 1.0 1.0 signature.png
    // 3. Unaligned signature: ! 1.0 1.0 signature.png or ! 1.0 1.0 WhiteBlankImage.png
    if (this.editor.schema.nodes.whiteBlankImage) {
      rules.push(
        new InputRule({
          find: /\{\s*(=>|==|<=|=)?\s*!\s*([^\s]+)\s+([^\s]+)\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*)\s*\[\s*([^\]]+)\s*\]\s*\}\s*$/i,
          handler: ({ state, range, match }) => {
            const { tr } = state;
            const alignStr = match[1];
            const align = (alignStr === '=>' || alignStr === '=') ? 'right' : alignStr === '==' ? 'center' : 'left';
            const width = match[2] || '1.0';
            const height = match[3] || '1.0';
            const filename = match[4] || 'signature.png';
            const condition = match[5]?.trim() || '';
            const node = state.schema.nodes.whiteBlankImage.create({
              width,
              height,
              filename,
              align,
              condition,
              conditionData: JSON.stringify({
                operator: '&&',
                rules: [{ field: condition.replace(/<<|>>/g, '').split('==')[0].trim(), operator: 'equals', value: (condition.split('==')[1] || '').trim() }],
                label: condition,
              }),
              ruleId: `sig_${Date.now()}`,
            });
            tr.replaceWith(range.from, range.to, node);
          },
        })
      );

      rules.push(
        new InputRule({
          find: /(?:(=>|==|<=)\s*)!\s*([^\s]+)\s+([^\s]+)\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*)\s*$/i,
          handler: ({ state, range, match }) => {
            const { tr } = state;
            const alignStr = match[1];
            const align = alignStr === '=>' ? 'right' : alignStr === '==' ? 'center' : 'left';
            const width = match[2] || '1.0';
            const height = match[3] || '1.0';
            const filename = match[4] || 'signature.png';
            const node = state.schema.nodes.whiteBlankImage.create({
              width,
              height,
              filename,
              align,
            });
            tr.replaceWith(range.from, range.to, node);
          },
        })
      );

      rules.push(
        new InputRule({
          find: /!\s*([^\s]+)\s+([^\s]+)\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*)\s*$/i,
          handler: ({ state, range, match }) => {
            const { tr } = state;
            const width = match[1] || '1.0';
            const height = match[2] || '1.0';
            const filename = match[3] || 'signature.png';
            const node = state.schema.nodes.whiteBlankImage.create({
              width,
              height,
              filename,
            });
            tr.replaceWith(range.from, range.to, node);
          },
        })
      );
    }

    // Note: <page break> and <line> commands are recognized and handled by SmartSyntaxExtension
    // with single/double angle bracket protection and table support.

    return rules;
  },

  addProseMirrorPlugins() {
    const pluginKey = new PluginKey('syntaxAutoSyncPlugin');

    return [
      new Plugin({
        key: pluginKey,
        props: {
          handleTextInput(view, from, to, text) {
            if (text !== '}' && text !== ']') return false;

            const { state } = view;
            const $from = state.doc.resolve(from);
            const parent = $from.parent;
            const blockStart = $from.start();
            const blockEnd = $from.end();

            // 1. Mark step-out behavior if cursor is exiting an existing mark
            const optMarkType = state.schema.marks.optionalBlock;
            const condMarkType = state.schema.marks.conditionalBlock;
            const hasOpt = optMarkType && (optMarkType.isInSet($from.marks()) || (state.storedMarks && optMarkType.isInSet(state.storedMarks)));
            const hasCond = condMarkType && (condMarkType.isInSet($from.marks()) || (state.storedMarks && condMarkType.isInSet(state.storedMarks)));

            if (text === '}' && (hasOpt || hasCond)) {
              let markEnd = from;
              state.doc.nodesBetween(blockStart, blockEnd, (node, pos) => {
                if (node.isInline && pos >= blockStart) {
                  const m = node.marks?.find(m => (hasOpt && m.type === optMarkType) || (hasCond && m.type === condMarkType));
                  if (m) {
                    markEnd = Math.max(markEnd, pos + node.nodeSize);
                  }
                }
              });

              if (from >= markEnd - 1) {
                const tr = state.tr;
                tr.setSelection(TextSelection.near(tr.doc.resolve(markEnd)));
                if (optMarkType) tr.removeStoredMark(optMarkType);
                if (condMarkType) tr.removeStoredMark(condMarkType);
                view.dispatch(tr);
                return true;
              }
            }

            // 2. Build text representation and position map for the current textblock
            let textContent = '';
            const posMap = [];
            parent.forEach((child, offset) => {
              const childPos = blockStart + offset;
              if (child.isText) {
                const t = child.text || '';
                for (let i = 0; i < t.length; i++) {
                  posMap.push(childPos + i);
                  textContent += t[i];
                }
              } else if (child.type.name === 'variableTag') {
                const vText = `<<${child.attrs.label || child.attrs.variableKey}>>`;
                for (let i = 0; i < vText.length; i++) {
                  posMap.push(childPos);
                  textContent += vText[i];
                }
              }
            });

            if (!textContent.includes('{')) return false;

            // Find matching '{' before cursor
            const cursorIdx = Math.max(0, from - blockStart);
            const openIdx = textContent.lastIndexOf('{', cursorIdx);
            if (openIdx === -1) return false;

            // Determine closing '}'
            let closeIdx = textContent.indexOf('}', openIdx);
            if (text === '}' && (closeIdx === -1 || closeIdx < cursorIdx)) {
              closeIdx = cursorIdx;
            }

            if (closeIdx === -1) return false;

            const inner = textContent.substring(openIdx + 1, closeIdx);
            if (!inner.trim()) return false;

            const openBracePos = posMap[openIdx];
            const closeBracePos = posMap[closeIdx] !== undefined ? posMap[closeIdx] : from;
            if (openBracePos === undefined) return false;

            // Check for condition bracket [ ... ]
            const bkMatch = /\[([^\]\n]+)\]/.exec(inner);
            if (bkMatch && isConditionBracket(bkMatch[1])) {
              const bkStartIdx = openIdx + 1 + bkMatch.index;
              const bkEndIdx = bkStartIdx + bkMatch[0].length;
              const bracketStartPos = posMap[bkStartIdx];
              const bracketEndPos = (posMap[bkEndIdx - 1] !== undefined ? posMap[bkEndIdx - 1] + 1 : bracketStartPos + bkMatch[0].length);

              const schema = state.schema;
              if (!schema.marks.conditionalBlock) return false;

              const parsed = parseConditionString(bkMatch[1].trim());
              const ruleId = 'rule_' + Date.now() + '_' + Math.random().toString(36).substring(7);
              const cleanLabel = bkMatch[1]
                .replace(/^condition\s*:\s*/i, '')
                .replace(/\s+EXISTS\b/gi, '')
                .replace(/\s+exists\b/gi, '')
                .replace(/<<|>>/g, '')
                .trim();

              const condMark = schema.marks.conditionalBlock.create({
                ruleId,
                operator: parsed.operator,
                rules: parsed.rules,
                label: cleanLabel || 'Condition',
              });

              const tr = state.tr;
              // If an existing auto-closed '}' is at closeBracePos
              if (closeBracePos < tr.doc.content.size && tr.doc.textBetween(closeBracePos, closeBracePos + 1) === '}') {
                tr.delete(closeBracePos, closeBracePos + 1);
              }

              // 1. Delete [Condition]
              tr.delete(bracketStartPos, bracketEndPos);

              // 2. Delete '{'
              tr.delete(openBracePos, openBracePos + 1);

              // 3. Mark remaining statement with conditionalBlock
              const newStart = openBracePos;
              const newEnd = tr.mapping.map(closeBracePos);

              if (newEnd > newStart) {
                tr.addMark(newStart, newEnd, condMark);
              } else {
                tr.insertText('\u00A0', newStart);
                tr.addMark(newStart, newStart + 1, condMark);
              }

              tr.removeStoredMark(condMark.type);
              tr.setSelection(TextSelection.near(tr.doc.resolve(Math.max(newStart + 1, newEnd))));
              view.dispatch(tr);
              return true;
            } else if (text === '}' && !inner.includes('[')) {
              // Only convert to optionalBlock if user typed '}' and there is NO '[' inside
              const schema = state.schema;
              if (!schema.marks.optionalBlock) return false;
              const optionalMark = schema.marks.optionalBlock.create();

              const tr = state.tr;
              if (closeBracePos < tr.doc.content.size && tr.doc.textBetween(closeBracePos, closeBracePos + 1) === '}') {
                tr.delete(closeBracePos, closeBracePos + 1);
              }

              tr.delete(openBracePos, openBracePos + 1);

              const newStart = openBracePos;
              const newEnd = tr.mapping.map(closeBracePos);

              if (newEnd > newStart) {
                tr.addMark(newStart, newEnd, optionalMark);
              } else {
                tr.insertText('\u00A0', newStart);
                tr.addMark(newStart, newStart + 1, optionalMark);
              }

              tr.removeStoredMark(optionalMark.type);
              tr.setSelection(TextSelection.near(tr.doc.resolve(Math.max(newStart + 1, newEnd))));
              view.dispatch(tr);
              return true;
            }

            return false;
          },
          handleKeyDown(view, event) {
            if (event.key === 'ArrowRight') {
              const { state } = view;
              const { selection } = state;
              if (selection.empty) {
                const optMarkType = state.schema.marks.optionalBlock;
                const condMarkType = state.schema.marks.conditionalBlock;
                const hasOpt = optMarkType && (optMarkType.isInSet(selection.$from.marks()) || (state.storedMarks && optMarkType.isInSet(state.storedMarks)));
                const hasCond = condMarkType && (condMarkType.isInSet(selection.$from.marks()) || (state.storedMarks && condMarkType.isInSet(state.storedMarks)));
                if (hasOpt || hasCond) {
                  const $from = selection.$from;
                  const blockStart = $from.start();
                  const parentEnd = $from.end();
                  let markEnd = $from.pos;
                  state.doc.nodesBetween(blockStart, parentEnd, (node, pos) => {
                    if (node.isInline && pos >= blockStart) {
                      const m = node.marks?.find(m => (hasOpt && m.type === optMarkType) || (hasCond && m.type === condMarkType));
                      if (m) {
                        markEnd = Math.max(markEnd, pos + node.nodeSize);
                      }
                    }
                  });
                  if ($from.pos >= markEnd) {
                    const tr = state.tr;
                    if (optMarkType) tr.removeStoredMark(optMarkType);
                    if (condMarkType) tr.removeStoredMark(condMarkType);
                    view.dispatch(tr);
                  }
                }
              }
            }
            return false;
          },
        },
      }),
    ];
  },
});
